/* eslint-disable no-useless-escape */
import * as Yup from 'yup'
import regex from '@bbl-digital/snorre/lib/globals/validation/regularExpressions'
import { decodeHtmlEntities, htmlToText } from './html-helpers'
import { isValidAccountNumber } from '@bbl-digital/snorre'
import { isValid, parseISO, startOfTomorrow } from 'date-fns'

const numberMaxLength = 1000000

export const CommentValidation = Yup.string()
  .min(5, 'Må inneholde minst 5 tegn')
  .max(500, 'Kan inneholde max 500 tegn')

export const CommentValidationRequired =
  CommentValidation.required('Påkrevd felt')

export const TitleValidation = Yup.string()
  .min(5, 'Må inneholde minst 5 tegn')
  .max(100, 'Kan inneholde max 100 tegn')
  .required('Påkrevd felt')

export const DateValidation = Yup.string().required('Ugyldig dato')

export const DateMinimumTomorrowValidation = Yup.date()
  .transform((value, raw) => {
    if (typeof raw === 'string') {
      const parsed = parseISO(raw)
      return isValid(parsed) ? parsed : new Date('')
    }
    return value
  })
  .typeError('Ugyldig dato')
  .min(startOfTomorrow(), 'Dato må settes til minst en dag frem i tid')
  .required('Ugyldig dato')

export const RequiredValidation = Yup.string().trim().required('Påkrevd felt')

export const HtmlByMinLengthValidation = (min: number) =>
  Yup.string()
    .trim()
    .test(
      'no-emoji',
      'Kan ikke inneholde emojis',
      (value) => !hasEmojiInString(value || '')
    )
    .test(
      'no-text',
      'Påkrevd felt',
      (value) => htmlToText(String(value)).length !== 0
    )
    .test('min-text', 'Må inneholde minst 10 tegn', (value) => {
      if (!value) return false

      const cleanedFromTags = htmlToText(String(value))
      const removeHtmlEntities = decodeHtmlEntities(cleanedFromTags)
      return removeHtmlEntities.length > min
    })

export const NoValidation = Yup.string()

export const BooleanValidation = Yup.boolean()

export const NumberValidation = RequiredValidation.test(
  'isValid',
  'Ugyldig tall',
  (value) => {
    const number = Number(value)
    // only accept whole number
    if (value && !value.match(regex.exclusiveNumbers)) return false

    return !isNaN(number) && number > 0 && number <= numberMaxLength
  }
)

export const PriceMaxDigitsValidation = (maxDigits: number) =>
  Yup.string()
    .nullable(true)
    .when('isAdvisor', {
      is: true,
      then: (schema) =>
        schema
          .test(
            'noLettersAndMaxDigits',
            `Kan bare inneholde tall og maks ${maxDigits} siffer`,
            (value) => hasNoLettersAndMaxDigits(value || '', maxDigits)
          )
          .max(maxDigits, `Kan ikke inneholde mer enn ${maxDigits} siffer`),
      otherwise: (schema) => schema,
    })

export const BankAccountValidation = Yup.string()
  .nullable(true) // Always allow null values
  .optional() // Allow empty strings
  .when('isAdvisor', {
    is: true,
    then: (schema) =>
      schema
        .optional()
        .matches(/^\d+(\.\d+)*$/, {
          message: 'Kan bare inneholde tall',
          excludeEmptyString: true,
        })
        .test(
          'length',
          'Må inneholde 11 siffer (ekskluderer punktum)',
          (value) => {
            if (!value) return true // Skip validation if empty
            const numDigits = value.replace(/\./g, '').length
            return numDigits === 11
          }
        )
        .test('isValid', 'Ikke et gyldig kontonummer', (value) => {
          if (!value) return true
          if (typeof value !== 'string') return false
          const cleanValue = value.replace(/\./g, '')
          return isValidAccountNumber(cleanValue)
        }),
    otherwise: (schema) => schema, // No validation when isAdvisor is false
  })

export const NumberValidationNotRequired = Yup.string().test(
  'isValid',
  'Ugyldig tall',
  (value) => {
    if (!value) return true
    if (!value.match(regex.exclusiveNumbers)) return false

    const number = Number(value)

    return !isNaN(number) && number >= 0 && number <= numberMaxLength
  }
)

export const BoardContactFirstNameValidation = RequiredValidation.matches(
  /^[a-zA-Z\u00C0-\u00FF\-\' ]+$/,
  'Kan kun bruke bokstaver'
)
  .min(2, 'Må inneholde minst 2 bokstaver')
  .max(40, 'Kan inneholde max 40 bokstaver')

export const BoardContactLastNameValidation = RequiredValidation.matches(
  /^[a-zA-Z\u00C0-\u00FF\-\' ]+$/,
  'Kan kun bruke bokstaver'
)
  .min(2, 'Må inneholde minst 2 bokstaver')
  .max(80, 'Kan inneholde max 80 bokstaver')

export const PhoneValidation = Yup.string()
  .trim()
  .matches(regex.phonenumber, 'Ugyldig telefonnummer')
  .required('Påkrevd felt')

export const PhoneValidationNotRequired = Yup.string()
  .trim()
  .matches(regex.phonenumber, 'Ugyldig telefonnummer')
  .nullable()

export const EmailValidation = Yup.string()
  .trim()
  .max(100, 'Maks 100 tegn')
  .email('Ugyldig epost')
  .required('Påkrevd felt')

export const EmailValidationNullable = Yup.string()
  .trim()
  .max(100, 'Maks 100 tegn')
  .email('Ugyldig epost')
  .nullable()
  .required('Påkrevd felt')

export const EmailValidationNotRequired = Yup.string()
  .trim()
  .max(100, 'Maks 100 tegn')
  .email('Ugyldig epost')
  .nullable()

export const ZipCodeValidation = Yup.string()
  .matches(/^\d+$/, 'Postnummer kan bare inneholde tall')
  .length(4, 'Postnummer må inneholde 4 tall')
  .required('Påkrevd felt')

export const ZipCodeValidationInt = Yup.string()
  .min(1, 'Postnummer må inneholde 1-10 tall for internasjonale postnummer')
  .max(10, 'Postnummer må inneholde 1-10 tall for internasjonale postnummer')
  .required('Påkrevd felt')

export const CompanyNumberValidation = Yup.string()
  .matches(/^\d+$/, 'Kan bare inneholde tall')
  .length(9, 'Må inneholde 9 tall')
  .required('Påkrevd felt')

export const UrlValidation = Yup.string()
  .matches(regex.url, 'Ugyldig url')
  .required('Påkrevd felt')

export const AppPasswordValidation = Yup.string()
  .trim()
  .min(16, 'Minimum 16 tegn')
  .max(21, 'Maks 21 tegn')
  .required('Påkrevd felt')

export const PasswordValidation = Yup.string().required('Påkrevd felt')

export const SyncDateValidation = Yup.date().required('Påkrevd felt')

export const ResponsibleValidation = Yup.object().shape({
  fullName: Yup.string().test(
    'no-emoji',
    'Kan ikke inneholde emojis',
    (value) => !hasEmojiInString(value || '')
  ),
  id: Yup.string(),
})
export const ConnectedMeetingValidation = Yup.object().shape({
  id: Yup.string(),
  title: Yup.string(),
})
export const StringArrayValidation = Yup.array().of(Yup.string())

// This validation is until we convert the database fields over to NCLOB and NVARCHAR2
export const hasEmojiInString = (value: string) => {
  const emojiRegExp = new RegExp(
    /(\p{Emoji_Presentation}|\p{Extended_Pictographic})/gu
  )

  return Boolean(value.match(emojiRegExp)?.length)
}

export const hasNoLettersAndMaxDigits = (value: string, maxDigits: number) => {
  if (!value) return true
  const regex = new RegExp(`^\\d{1,${maxDigits}}$`)
  return Boolean(value.match(regex)?.length)
}
